<template>
  <div>
    <div class="text-right" style="margin-bottom: 5px">
      <b-button size="sm" variant="outline-success" @click="addNewToken">
        <i class="fa-solid fa-plus"></i>
        {{$t('operations.add')}}
      </b-button>
    </div>
    <b-table-simple hover small caption-top responsive v-handy-scroll>
      <b-thead>
        <b-tr>
          <b-th>{{$t('columns.token')}}</b-th>
          <b-th>{{$t('columns.creationTime')}}</b-th>
          <b-th>{{$t('columns.businessUnitId')}}</b-th>
          <b-th>{{$t('columns.representative')}}</b-th>
          <b-th>{{$t('columns.campaign')}}</b-th>
          <b-th>{{$t('columns.tag')}}</b-th>
          <b-th>{{$t('columns.active')}}</b-th>
          <b-th></b-th>
        </b-tr>
      </b-thead>
      <b-tbody v-if="!loading">
        <b-tr v-for="token in tokens" v-bind:key="token.id">
          <b-td>{{token.id}}</b-td>
          <b-td>{{token.creationTime | momentDate}}</b-td>
          <b-td>{{getOrganizationNameById(token.orgId)}} {{getBusinessUnitNameById(token.businessUnitId)}}</b-td>
          <b-td>{{getUserFullName(token.representativeId)}}</b-td>
          <b-td>{{token.campaignId}}</b-td>
          <b-td>{{token.tag}}</b-td>
          <b-td class="text-capitalize" :variant="token.active ? 'success': null">{{token.active ? $t('operations.yes') : $t('operations.no')}}</b-td>
          <b-td>
            <!-- Copy Button (legacy per-org API instructions) -->
            <b-button
                variant="outline-primary"
                size="sm"
                class="mr-1"
                :title="$t('copyAffiliateDocsLegacy')"
                @click="() => copyText(token)"
            >
              <i class="fa-solid fa-copy"></i>
            </b-button>

            <!-- Copy Button V2 (new global auto-distribution API instructions) -->
            <b-button
                variant="outline-info"
                size="sm"
                class="mr-1"
                :title="$t('copyAffiliateDocsV2')"
                @click="() => copyTextV2(token)"
            >
              <i class="fa-solid fa-copy"></i> V2
            </b-button>

            <!-- Enable / Disable Button -->
            <b-button variant="outline-primary" size="sm" class="mr-1" @click="() => toggleEnableToken(token)"><i class="fa-solid" :class="{ 'fa-plug': token.active, 'fa-plug-circle-minus': !token.active }"></i></b-button>

            <b-button
                variant="outline-warning"
                size="sm"
                @click="() => editToken(token)"
            >
              <i class="fa-solid fa-pen"></i>
            </b-button>

          </b-td>
        </b-tr>
      </b-tbody>
      <b-tbody v-else>
        <b-tr>
          <b-td colspan="8" class="text-center">
            <b-spinner>{{ $t('operations.loading') }}</b-spinner>
          </b-td>
        </b-tr>
      </b-tbody>
    </b-table-simple>
    <b-modal ref="newTokenModal" :title="$t('newToken')" centered size="lg" hide-footer>
      <b-form v-if="newToken">
        <b-row>
          <b-col cols="6" v-if="hasManyOrganizations">
            <b-form-group label-for="orgId">
              <label>{{$t('columns.orgId')}}</label>
              <organization-select-box class="small" v-model="newToken.orgId"/>
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-form-group label-for="businessUnitId">
              <label>{{$t('columns.businessUnitId')}}</label>
              <business-unit-select-box class="small" v-model="newToken.businessUnitId"/>
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-form-group label-for="representative">
              <label>{{$t('columns.representative')}}</label>
              <user-select-box class="small" v-model="newToken.representativeId"/>
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-form-group  label-for="campaign">
              <label>{{$t('columns.campaign')}}</label>
              <b-form-input size="sm" v-model="newToken.campaignId"/>
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-form-group label-for="tag">
              <label>{{$t('columns.tag')}}</label>
              <b-form-input size="sm" v-model="newToken.tag"/>
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-form-group label-for="active">
              <b-form-checkbox style="margin-top: 28px" v-model="newToken.active">{{$t('columns.active')}}</b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-row>
        <b-button size="sm" block variant="outline-success" @click="saveToken">{{$t('operations.save')}}</b-button>
      </b-form>
    </b-modal>


  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import AffiliateToken from '@/model/affiliate/AffiliateToken'
import userSelectBox from '@/components/widget/select/UserSelectBox.vue'
import OrganizationSelectBox from '@/components/widget/select/OrganizationSelectBox.vue'
import BusinessUnitSelectBox from '@/components/widget/select/BusinessUnitSelectBox.vue'
import mixinNotifications from '@/components/mixin/mixinNotifications'
export default {
  name: 'AffiliateTokenTable',
  props: ['affiliateId'],
  mixins: [mixinNotifications],
  components: { BusinessUnitSelectBox, OrganizationSelectBox, userSelectBox },
  watch: {
    affiliateId: {
      handler() {
        this.updateTokens()
      },
      immediate: true
    }
  },
  data: () => ({

    tokens: [],
    loading: true,
    newToken: null,
    isEditMode: false,
    currentToken: null, // used for both new and edit
  }),
  computed:{
    ...mapGetters('data', ['hasManyOrganizations', 'getOrganizationNameById', 'getOrganizationCrmId', 'getBusinessUnitNameById', 'getUserFullName']),
  },
  methods: {
    ...mapActions('affiliate', [
      'getAffiliateTokens',
      'createAffiliateToken',
      'updateAffiliateToken'
    ]),

    updateTokens() {
      this.tokens = []
      this.loading = true
      this.getAffiliateTokens(this.affiliateId)
          .then(response => (this.tokens = response || []))
          .finally(() => (this.loading = false))
    },

    // === OPEN MODAL FOR NEW TOKEN ===
    addNewToken() {
      this.isEditMode = false
      this.newToken = AffiliateToken({
        affiliateId: this.affiliateId,
        active: true
      })
      this.$refs.newTokenModal.show()
    },

    // === OPEN MODAL FOR EDIT TOKEN ===
    editToken(token) {
      this.isEditMode = true
      // clone token to avoid direct mutation on the table
      this.newToken = { ...token }
      this.$refs.newTokenModal.show()
    },

    // The docs concatenate "api/..." onto the organization's CRM URL setting.
    // That setting may be saved with or without a trailing slash — normalize
    // to exactly one so links never come out as "...comapi/...".
    apiBase(orgId) {
      const raw = this.getOrganizationCrmId(orgId) || ''
      return raw.replace(/\/+$/, '') + '/'
    },

    // === COPY TEXT ===
    async copyText(token) {
      try {
        const base = this.apiBase(token.orgId)
        const affiliateApiText =
            "Dear Affiliate,\n\n" +
            "Please use the following token to access the affiliate portal:\n\n" +
            token.id +
            "\n\n" +
            "Use this URL as API base:\n\n" +
            base +
            "\n\n" +
            "Authentication:\n" +
            "Send the token in the HTTP Authorization header prefixed with the word \"Bearer\" (standard Bearer scheme).\n\n" +
            "Header format:\n" +
            "Authorization: Bearer <your_token>\n\n" +
            "Example:\n" +
            "Authorization: Bearer " + token.id + "\n\n" +
            "Example cURL request:\n" +
            "curl -X GET \"" + base + "api/affiliate/...\" \\\n" +
            "     -H \"Authorization: Bearer " + token.id + "\" \\\n" +
            "     -H \"Content-Type: application/json\"\n\n" +
            "Documentation can be found at:\n" +
            base +
            "api/public/docs/swagger-ui/index.html?urls.primaryName=affiliate\n" +
            base +
            "api/public/docs/swagger-ui/index.html\n\n" +
            "Best regards,\n" +
            "Affiliate Management Team"
        await navigator.clipboard.writeText(affiliateApiText)
        this.showSuccessNotification(this.$t('affiliateDocsCopied'))
      } catch (err) {
        console.error('Failed to copy: ', err)
      }
    },

    // === COPY TEXT V2 (new global auto-distribution API) ===
    async copyTextV2(token) {
      try {
        const base = this.apiBase(token.orgId)
        const affiliateApiTextV2 =
            "Dear Affiliate,\n\n" +
            "Please use the following token to access the affiliate portal:\n\n" +
            token.id +
            "\n\n" +
            "Use this URL as API base:\n\n" +
            base +
            "\n\n" +
            "Authentication:\n" +
            "Send the token in the HTTP Authorization header prefixed with the word \"Bearer\" (standard Bearer scheme).\n\n" +
            "Header format:\n" +
            "Authorization: Bearer <your_token>\n\n" +
            "Example:\n" +
            "Authorization: Bearer " + token.id + "\n\n" +
            "Insert a new lead (global integration):\n" +
            "POST " + base + "api/public/affiliate-global/clients/insert\n\n" +
            "Required fields: firstName, lastName, phone, country (2-letter ISO code), email.\n" +
            "Optional fields: mobile, city, address, zip, lang, email2, birthday, gender, campaign, banner, tag, companyName, companyNumber, position, activity, comment.\n\n" +
            "Example cURL request:\n" +
            "curl -X POST \"" + base + "api/public/affiliate-global/clients/insert\" \\\n" +
            "     -H \"Authorization: Bearer " + token.id + "\" \\\n" +
            "     -H \"Content-Type: application/json\" \\\n" +
            "     -d '{\n" +
            "       \"firstName\": \"John\",\n" +
            "       \"lastName\": \"Doe\",\n" +
            "       \"email\": \"john.doe@example.com\",\n" +
            "       \"phone\": \"+1234567890\",\n" +
            "       \"country\": \"US\"\n" +
            "     }'\n\n" +
            "On success (HTTP 201) the response returns the created client under \"data\" and the client access token under \"metadata.token\".\n\n" +
            "For testing, start the first name or email with \"test\", or use an email ending with @example.com.\n\n" +
            "Documentation can be found at:\n" +
            base +
            "api/public/affiliate-docs\n\n" +
            "Best regards,\n" +
            "Affiliate Management Team"
        await navigator.clipboard.writeText(affiliateApiTextV2)
        this.showSuccessNotification(this.$t('affiliateDocsCopied'))
      } catch (err) {
        console.error('Failed to copy: ', err)
      }
    },

    // === SAVE (CREATE or UPDATE) ===
    saveToken() {
      this.loading = true

      // if in edit mode → update, otherwise create
      const action = this.isEditMode
          ? this.updateAffiliateToken(this.newToken)
          : this.createAffiliateToken(this.newToken)

      action
          .then(() => {
            this.updateTokens()
            this.$refs.newTokenModal.hide()
            this.showSuccessNotification(
                this.$t(
                    this.isEditMode
                        ? 'globalMessages.updateDataSuccess'
                        : 'globalMessages.saveDataSuccess',
                    { system: this.$t('token') }
                )
            )
          })
          .catch(e => {
            console.error(e)
            this.showErrorNotification(
                this.$t(
                    this.isEditMode
                        ? 'globalMessages.updateDataError'
                        : 'globalMessages.saveDataError',
                    { system: this.$t('token') }
                ),
                'Error saving token'
            )
          })
          .finally(() => (this.loading = false))
    },

    // === ENABLE / DISABLE ===
    toggleEnableToken(token) {
      token.active = !token.active
      this.loading = true
      this.updateAffiliateToken({ ...token })
          .then(() => this.updateTokens())
          .finally(() => (this.loading = false))
    }
  }

}
</script>

<style scoped>

</style>
